'use strict';

/**
 * Shared Portal fact dimensions for every node in one static flow slice (same `scope` + `workflow` + `pipeline`).
 *
 * @typedef {Object} PipelineStatusFlowBindDefaults
 * @property {string} scope e.g. `Project`
 * @property {string} workflow e.g. `Publishing`
 * @property {string} pipeline e.g. `V7`
 */

/**
 * Minimal node spec for authoring. Expanded to {@link import('./pipelineStatusTypes').PipelineStatusGraphStage}.
 *
 * @typedef {Object} PipelineStatusFlowNodeSpec
 * @property {string} id stable graph node id
 * @property {string} description card title copy
 * @property {string[]} [dependsOn] ids of predecessor nodes (default `[]`)
 * @property {string} producerStage Portal / SNS `stage` string (stored on facts; `factMatch.stage`)
 * @property {string} service producer name for fact bind key and card label (top-level `service` + `factMatch.service`)
 * @property {import('./pipelineStatusTypes').PipelineStatusIterationManifestMatch} [iterationManifestMatch]
 * @property {import('./pipelineStatusTypes').PipelineStatusManifestCompletionPolicy} [completionPolicy]
 */

/**
 * Expands flow defaults + minimal node specs into full graph stages for {@link buildPipelineStatusGraphViewModel}.
 *
 * @param {PipelineStatusFlowBindDefaults} defaults
 * @param {PipelineStatusFlowNodeSpec[]} nodes
 * @returns {import('./pipelineStatusTypes').PipelineStatusGraphStage[]}
 */
export function buildPipelineStatusGraphStagesFromFlow(defaults, nodes) {
	const scope = String(defaults?.scope ?? '').trim();
	const workflow = String(defaults?.workflow ?? '').trim();
	const pipeline = String(defaults?.pipeline ?? '').trim();
	if (!scope || !workflow || !pipeline) {
		throw new Error(
			'[pipelineStatus] Flow defaults require non-empty scope, workflow, and pipeline (trimmed strings).',
		);
	}

	return nodes.map((n, index) => {
		const id = String(n?.id ?? '').trim();
		const description = String(n?.description ?? '').trim();
		const producerStage = String(n?.producerStage ?? '').trim();
		const service = String(n?.service ?? '').trim();
		if (!id) {
			throw new Error(`[pipelineStatus] Flow node at index ${index}: id is required (non-empty after trim).`);
		}
		if (!description) {
			throw new Error(`[pipelineStatus] Flow node ${JSON.stringify(id)}: description is required.`);
		}
		if (!producerStage) {
			throw new Error(`[pipelineStatus] Flow node ${JSON.stringify(id)}: producerStage is required.`);
		}
		if (!service) {
			throw new Error(`[pipelineStatus] Flow node ${JSON.stringify(id)}: service is required.`);
		}
		const dependsOn = Array.isArray(n?.dependsOn) ? n.dependsOn.map((x) => String(x)) : [];
		const iterationManifestMatch =
			n?.iterationManifestMatch != null && typeof n.iterationManifestMatch === 'object'
				? n.iterationManifestMatch
				: undefined;
		const completionPolicy =
			n?.completionPolicy != null && String(n.completionPolicy).trim() !== ''
				? n.completionPolicy
				: undefined;

		return {
			id,
			description,
			dependsOn,
			service,
			factMatch: {
				scope,
				workflow,
				pipeline,
				stage: producerStage,
				service,
			},
			...(iterationManifestMatch ? { iterationManifestMatch } : {}),
			...(completionPolicy ? { completionPolicy } : {}),
		};
	});
}
